import { useRef, useState } from "react";

import {
    Button,
    Form,
    Input,
    Modal,
    Space,
    Icons,
    notificationProvider,
    useModalForm,
} from "@pankod/refine-antd";
import NestsxCrud from "dataProvider/nestjs-crud";
import { API_URL } from "App";


export const SeedingUploadCsvModal: React.FC<{label: string, apiPath: string, sampleCsvPath?: string}> = ({ label, apiPath, sampleCsvPath }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [companyId, setCompanyId] = useState('');

    const handleIconClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDownloadSample = async () => {
        if (!sampleCsvPath) {
            notificationProvider.open({ message: 'Sample CSV path not configured', type: 'error', description: '' });
            return;
        }
        try {
            const token = sessionStorage.getItem("konsole-token");
            const response = await fetch(`${API_URL}${sampleCsvPath}`, {
                headers: { authorization: token ? `Bearer ${token}` : "" },
            });
            if (!response.ok) {
                notificationProvider.open({ message: 'Failed to download sample CSV', type: 'error', description: '' });
                return;
            }
            const { csv, fileName } = await response.json();
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } catch (err: any) {
            notificationProvider.open({ message: err?.message || 'Download failed', type: 'error', description: '' });
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (file.type !== 'text/csv') {
            notificationProvider.open({ message: 'Please upload a CSV file.', type: 'error', description: '' });
            return;
        }
        if (!companyId) {
            notificationProvider.open({ message: 'Please enter a Company ID first.', type: 'error', description: '' });
            return;
        }
        try {
            const token = sessionStorage.getItem("konsole-token");
            const formData = new FormData();
            formData.append('file', file);
            formData.append('companyId', companyId);

            const response = await fetch(`${API_URL}${apiPath}`, {
                method: 'post',
                headers: { authorization: token ? `Bearer ${token}` : "" },
                body: formData,
            });

            if (!response.ok) {
                notificationProvider.open({ message: 'Upload failed', type: 'error', description: '' });
                return;
            }

            notificationProvider.open({ message: `Successfully uploaded for '${label}'`, type: 'success', description: 'Upload complete' });
            SeedingCSVUploadClose();
        } catch (err: any) {
            notificationProvider.open({ message: err?.message || 'Upload failed', type: 'error', description: '' });
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    let {
        modalProps: SeedingCSVUploadModal,
        formProps: SeedingCSVUploadForm,
        show: SeedingCSVUploadShow,
        close: SeedingCSVUploadClose
    } = useModalForm({
        resource: apiPath,
        dataProviderName: "grapghQl",
        action: "create",
        metaData: { },
        redirect: false,
        onMutationSuccess() {
            SeedingCSVUploadClose();
        }
    });
    SeedingCSVUploadModal.title = label;
    SeedingCSVUploadModal.centered = true;
    SeedingCSVUploadModal.width = 500;
    SeedingCSVUploadModal.footer = null;
    SeedingCSVUploadModal.onCancel = () => {
        SeedingCSVUploadForm.form?.resetFields();
        setCompanyId('');
        SeedingCSVUploadClose();
    };

    return (
        <Space>
            <Button type="primary" onClick={() => { SeedingCSVUploadShow(); }}>
                {label}
            </Button>

            <Modal {...SeedingCSVUploadModal}>
                <Form {...SeedingCSVUploadForm} layout="vertical">
                    <Form.Item
                        label="Company Id"
                        name="companyId"
                        rules={[{ required: true, message: 'Please enter a Company ID.' }]}
                    >
                        <Input onChange={(e) => setCompanyId(e.target.value)} />
                    </Form.Item>
                    <Form.Item label="Upload CSV">
                        <Space>
                            <Icons.UploadOutlined
                                onClick={companyId ? handleIconClick : undefined}
                                style={{ cursor: companyId ? 'pointer' : 'not-allowed', fontSize: 20, opacity: companyId ? 1 : 0.4 }}
                            />
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept=".csv"
                                onChange={handleFileChange}
                            />
                        </Space>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button
                                type="link"
                                onClick={handleDownloadSample}
                                disabled={!sampleCsvPath}
                            >
                                Download Sample
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};
